// Constants
import { formActionNaming } from '../components/forms/ActionForms/constants';

// Utils
import { decompressSessionStorage } from './sessionStorage';
import { claimDraftScope, type DraftStore } from './draftOwnership';
import {
  ACTION_VALIDATION_DRAFT_KEYS,
  ACTION_VALIDATION_SCOPE,
} from './draftScopes';

/**
 * Contexto por pestaña del asistente de VALIDACIÓN de una actuación
 * (`new-validation-action-data` + las cachés de cada pestaña).
 *
 * 🔴 **La URL manda.** Este contexto lo escriben solo algunas puertas (el ojo
 * de la tarjeta, la tabla de Validación, la lista de Actuaciones, Gestión
 * Horaria) y lo leían TODOS los loaders del asistente, la miga y el guardado
 * ANTES que el `:id` de la URL. Entrar después por una puerta que no lo
 * escribe (el enlace «Act. básica» de la tabla de Tramos, la pill «Resuelto
 * por», atrás, recargar, una URL compartida) dejaba el de la actuación
 * ANTERIOR: el resumen pintaba la de la URL, pero la miga, el asistente y el
 * guardado trabajaban sobre la otra — incluso de otro recorrido, y Partidas
 * escribía en las dos a la vez. Reproducido en prod el 2026-10-08:
 * `docs/superpowers/specs/2026-10-08-rv-parciales-diagnostico.md` § 8.
 *
 * Por eso nadie lee el contexto a pelo: se pide con
 * `reconcileValidationActionContext(urlActionId)`, que solo lo devuelve si es
 * de la actuación de la URL y, si no, lo BORRA entero para que el llamante
 * cargue por URL (el camino que ya existía para recargas y enlaces directos).
 */
export type ValidationActionContext = {
  id?: string;
  orderId?: string;
  roid?: string;
  entityRoid?: string;
  [key: string]: unknown;
};

/**
 * Id de la actuación a la que pertenecen las copias de las pestañas del
 * asistente de validación (2026-11-02).
 *
 * Las copias no dicen de quién son, y el contexto principal solo lo escriben
 * algunas puertas: entrar por el recorrido (enlace «Act. básica» de Tramos,
 * «Resuelto por»), recargar o una URL directa no lo dejan. Sin dueño, las
 * copias de la actuación anterior se colaban en la siguiente — Ubicación y
 * Datos Act enseñaban su PK y su descripción, y el guardado los mandaba sobre
 * la de la URL.
 *
 * Desde la pieza común es el dueño del ámbito `ACTION_VALIDATION_SCOPE`
 * (`src/utils/draftScopes.ts`); se conserva el nombre para quien ya lo usa.
 * Spec: `docs/superpowers/specs/2026-11-02-wizard-draft-ownership-design.md`.
 */
export const VALIDATION_CACHE_OWNER_KEY = ACTION_VALIDATION_SCOPE.ownerKey;

/**
 * Escribe el dueño de las copias. `setItem` puede lanzar (almacén lleno), y
 * `reconcile…` corre al principio de 9 loaders y en el render del resumen: un
 * fallo aquí no puede tumbar la pantalla. Sin dueño escrito, la próxima carga
 * vuelve a descartar las copias — se pierde lo no guardado, nunca se cuela.
 */
const writeOwner = (
  store: Pick<Storage, 'setItem'>,
  actionId: string
): void => {
  try {
    store.setItem(VALIDATION_CACHE_OWNER_KEY, actionId);
  } catch {
    /* almacén lleno o bloqueado: ver arriba */
  }
};

/**
 * Borra TODO el contexto de validación de la pestaña: la actuación elegida,
 * las cachés de cada pestaña del asistente, los cambios de externas y el
 * dueño de las copias.
 *
 * No toca `new-action-data`: es el puntero del asistente de CREACIÓN, que
 * `ShowOT` usa para descartar el borrador anterior.
 */
export const clearValidationActionStorage = (
  store: Pick<Storage, 'removeItem'> = sessionStorage
): void => {
  store.removeItem(formActionNaming.NEW_VALIDATION_ACTION_DATA);
  ACTION_VALIDATION_DRAFT_KEYS.forEach((key) => store.removeItem(key));
  store.removeItem(VALIDATION_CACHE_OWNER_KEY);
};

const parseContext = (raw: string): ValidationActionContext | null => {
  try {
    const parsed = JSON.parse(decompressSessionStorage(raw) ?? 'null');
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Devuelve el contexto de validación SOLO si es de la actuación `urlActionId`.
 * Si es de otra, no tiene id o no se puede leer, borra todo el contexto de la
 * pestaña y devuelve `null`.
 *
 * Además deja las copias de las pestañas SOLO si son de `urlActionId`, y la
 * deja de dueña (`VALIDATION_CACHE_OWNER_KEY`). Sin contexto principal —entrar
 * por el recorrido, recargar, una URL— antes no se borraba nada y se colaban
 * las de la actuación anterior. Las copias las descarta la pieza común
 * (`claimDraftScope(ACTION_VALIDATION_SCOPE, …)`); aquí solo queda la regla
 * del puntero de contexto, que es propia de este asistente.
 *
 * 🔴 Llamarlo de forma SÍNCRONA al principio del loader, antes de cualquier
 * `await`: React Router lanza en paralelo los loaders de la ruta `:id` y de la
 * pestaña, y así todos limpian antes de que ninguno escriba sus cachés. Es
 * idempotente — llamarlo varias veces con el mismo id da lo mismo.
 */
export const reconcileValidationActionContext = (
  urlActionId: string | undefined,
  store: DraftStore = sessionStorage
): ValidationActionContext | null => {
  // 🔴 Nunca lanza (Ruling 20): es la primera línea de los loaders de validar.
  // Con el almacén bloqueado se va al lado SEGURO: sin contexto que devolver y
  // con el reclamo (`claimDraftScope`, que tampoco lanza) tratando las copias
  // como ajenas — nunca se dan por buenas las de otra actuación.
  const read = (key: string): { ok: boolean; value: string | null } => {
    try {
      return { ok: true, value: store.getItem(key) };
    } catch {
      return { ok: false, value: null };
    }
  };
  const remove = (key: string): void => {
    try {
      store.removeItem(key);
    } catch {
      /* bloqueado: no se puede quitar; el reclamo descarta lo que pueda */
    }
  };

  const contextRead = read(formActionNaming.NEW_VALIDATION_ACTION_DATA);
  if (!contextRead.ok) {
    if (urlActionId) claimDraftScope(ACTION_VALIDATION_SCOPE, urlActionId, store);
    return null;
  }
  const raw = contextRead.value;
  const context = raw ? parseContext(raw) : null;

  // Sin id de URL no hay con qué comparar: se respeta lo guardado (no hay
  // ruta de validación sin `:id`, pero no se borra nada por las dudas).
  if (!urlActionId) return context;

  if (raw && !(context?.id && context.id === urlActionId)) {
    // Contexto de OTRA actuación (o sin id, o ilegible): fuera todo. Se quita
    // también el dueño para que el reclamo descarte las copias aunque dijera
    // ser de la URL — con el contexto de otra, no se puede fiar.
    remove(formActionNaming.NEW_VALIDATION_ACTION_DATA);
    remove(VALIDATION_CACHE_OWNER_KEY);
    claimDraftScope(ACTION_VALIDATION_SCOPE, urlActionId, store);
    return null;
  }

  // Contexto de esta URL y sin dueño: las copias son suyas — quien escribe el
  // contexto (ojo de la tarjeta, tabla de Validación…) borra antes. Se queda
  // de dueña sin reclamar: el reclamo, sin dueño, las descartaría. Si el dueño
  // no se puede leer, no se presume nada: lo decide el reclamo de abajo.
  const ownerRead = read(VALIDATION_CACHE_OWNER_KEY);
  if (context && ownerRead.ok && !ownerRead.value) {
    writeOwner(store, urlActionId);
    return context;
  }

  // Sin contexto o con dueño de OTRA actuación (sesión anterior al arreglo,
  // APK restaurada, entrar por el recorrido) no se puede saber de quién son:
  // el reclamo las descarta. Con dueño = URL no toca nada.
  claimDraftScope(ACTION_VALIDATION_SCOPE, urlActionId, store);

  return context;
};

/**
 * Para las pantallas que NO son del asistente de validación pero comparten
 * loader y componente con él: el resumen de CREACIÓN (`new-action/summary/:id`).
 *
 * Solo quita el PUNTERO de contexto (`new-validation-action-data`) si es de
 * otra actuación, lo que hacía la base; devuelve el contexto si es de
 * `urlActionId`. NO reclama el ámbito de validación, ni toca su dueño ni sus
 * copias (Ruling 17): reclamarlo con el id de la actuación recién creada tiraba
 * lo editado sin guardar de OTRA — validar X entrando por Tramos, crear una
 * básica con el cronómetro, volver a X: perdido. Las copias siguen con su
 * dueño y la puerta de validación las comprueba al volver.
 *
 * Síncrono y nunca lanza (el almacén puede estar bloqueado).
 */
export const releaseForeignValidationPointer = (
  urlActionId: string | undefined,
  store: Pick<Storage, 'getItem' | 'removeItem'> = sessionStorage
): ValidationActionContext | null => {
  try {
    const raw = store.getItem(formActionNaming.NEW_VALIDATION_ACTION_DATA);
    if (!raw) return null;
    const context = parseContext(raw);
    if (!urlActionId) return context;
    if (context?.id && context.id === urlActionId) return context;
    store.removeItem(formActionNaming.NEW_VALIDATION_ACTION_DATA);
  } catch {
    // Almacén bloqueado: no hay puntero que leer ni que quitar.
  }
  return null;
};

/**
 * ¿Pertenece este contexto (de actuación, accidente o comunicación) a la
 * pantalla actual? Solo si su id aparece en la URL. Es la misma regla que ya
 * aplica la miga de Redux (`utils/breadcrumbs.ts`): un contexto que no está en
 * la URL es de una pantalla anterior y no debe pintarse.
 */
export const contextBelongsToPath = (
  context: { id?: unknown } | null | undefined,
  pathname: string
): boolean =>
  typeof context?.id === 'string' &&
  context.id.length > 0 &&
  pathname.split('/').includes(context.id);
